import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useLocation } from 'react-router'
import { toast } from 'sonner'

import { getSocket } from '@/api/socket'

import { installMessagingSocketHandlers } from './socket-handlers'

/** called once at app boot inside the authenticated tree (RegisterUser).
 * installs the messaging:* socket listeners and tears them down on
 * unmount. does NOT own the socket lifecycle . the notifications
 * bootstrap is the connect/disconnect owner; we share the same singleton.
 *
 * surfaces sonner toasts for incoming messages but only when the user
 * isn't already looking at that conversation's thread (otherwise the toast
 * is noisy . they can see the message land in the panel).
 */
export function useMessagingBootstrap(): void {
  const queryClient = useQueryClient()
  const location = useLocation()

  useEffect(() => {
    const socket = getSocket()
    const detach = installMessagingSocketHandlers(
      socket,
      queryClient,
      (conversationId, message) => {
        /** only toast when the user is not actively viewing that thread.
         * route shape: /messages or /messages/:conversationId. on
         * /messages/:id matching the incoming one, no toast (the message
         * is already visible in the panel via the cache append).
         */
        const path = location.pathname
        if (
          path === `/messages/${conversationId}` ||
          path.startsWith(`/messages/${conversationId}/`)
        ) {
          return
        }
        const title = message.senderUserName
          ? `${message.senderUserName} sent a message`
          : 'New message'
        toast(title, {
          description: message.body.slice(0, 120),
          action: {
            label: 'Open',
            onClick: () => {
              window.location.assign(`/messages/${conversationId}`)
            }
          }
        })
      }
    )
    return () => {
      detach()
    }
    /** re-install when the pathname changes so the closure over
     * location.pathname stays current. small cost: a couple of
     * socket.on/off pairs per nav, fine for the volumes involved.
     */
  }, [queryClient, location.pathname])
}
